import type { TimedModeId } from "./gameplay-config";

export type DailyChallengeIdentity = `daily:v${number}:${string}`;

export interface DailyChallengeDefinition {
  readonly date: string;
  readonly displayDate: string;
  readonly identity: DailyChallengeIdentity;
  readonly mode: TimedModeId;
  readonly seed: string;
  readonly version: number;
}

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const dailyIdentityPattern = /^daily:v([1-9][0-9]*):(\d{4})-(\d{2})-(\d{2})$/u;

const formatDate = (date: Date): string => {
  const year = String(date.getUTCFullYear()).padStart(4, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isDailyChallengeIdentity = (
  value: unknown,
): value is DailyChallengeIdentity => {
  if (typeof value !== "string") {
    return false;
  }
  const match = dailyIdentityPattern.exec(value);
  if (match === null) {
    return false;
  }
  const version = Number(match[1]);
  const year = Number(match[2]);
  const month = Number(match[3]);
  const day = Number(match[4]);
  if (!Number.isSafeInteger(version)) {
    return false;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

export const createDailyChallengeDefinition = (
  nowMs: number,
  version = 1,
): DailyChallengeDefinition => {
  if (!Number.isFinite(nowMs)) {
    throw new RangeError("Daily challenge clock is invalid");
  }
  if (!Number.isSafeInteger(version) || version < 1) {
    throw new RangeError("Daily challenge version is invalid");
  }
  const date = new Date(nowMs);
  const dateKey = formatDate(date);
  const monthName = monthNames[date.getUTCMonth()];
  if (monthName === undefined) {
    throw new RangeError("Daily challenge clock is invalid");
  }
  const versionLabel = `v${String(version)}`;
  return {
    date: dateKey,
    displayDate: `${monthName} ${String(date.getUTCDate())}`,
    identity: `daily:${versionLabel}:${dateKey}` as DailyChallengeIdentity,
    mode: "challenge-1",
    seed: `snakely:daily:${versionLabel}:${dateKey}`,
    version,
  };
};
